import React, { useState, useEffect, useCallback } from 'react';
import { Bell, User, Search, LogOut, Settings, ChevronDown, Warehouse, AlertTriangle, DollarSign } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import apiService from '../../services/apiService';
import './Header.css';

const Header = ({ title = 'Dashboard' }) => {
  const { user, logout, activeWarehouse, setActiveWarehouse } = useApp();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showWarehouseMenu, setShowWarehouseMenu] = useState(false);

  // Estado para alerta de efectivo
  const [alertaEfectivo, setAlertaEfectivo] = useState({
    totalEfectivo: 0,
    limiteMaximo: 0,
    excedido: false,
    loading: true
  });

  // Auto-seleccionar primer almacén si el usuario tiene almacenes y no hay uno activo
  useEffect(() => {
    if (user && user.almacenes && user.almacenes.length > 0 && !activeWarehouse) {
      // Buscar almacén principal o usar el primero
      const principalWarehouse = user.almacenes.find(a => a.es_principal);
      setActiveWarehouse(principalWarehouse || user.almacenes[0]);
    }
  }, [user, activeWarehouse, setActiveWarehouse]);

  // Función para cargar alerta de efectivo
  const cargarAlertaEfectivo = useCallback(async () => {
    try {
      const nombrePc = localStorage.getItem('pos_nombre_pc');
      if (!nombrePc) {
        setAlertaEfectivo(prev => ({ ...prev, loading: false }));
        return;
      }

      // Cargar totales de caja y configuración en paralelo
      const [totalesResult, configResult] = await Promise.all([
        apiService.getVentasPendientesCorte(nombrePc),
        apiService.getConfiguracionesPorCategoria('caja', nombrePc)
      ]);

      const totalEfectivo = totalesResult.success ? (totalesResult.data?.totales?.total_efectivo || 0) : 0;
      const limiteMaximo = configResult.success ? (parseFloat(configResult.data?.valores?.caja_alerta_efectivo_maximo) || 0) : 0;
      const excedido = limiteMaximo > 0 && totalEfectivo > limiteMaximo;

      setAlertaEfectivo({
        totalEfectivo,
        limiteMaximo,
        excedido,
        loading: false
      });
    } catch (error) {
      console.error('Error cargando alerta de efectivo:', error);
      setAlertaEfectivo(prev => ({ ...prev, loading: false }));
    }
  }, []);

  // Cargar alerta de efectivo al montar y cada 60 segundos
  useEffect(() => {
    cargarAlertaEfectivo();

    // Actualizar cada 60 segundos
    const interval = setInterval(cargarAlertaEfectivo, 60000);

    // Escuchar evento personalizado para actualizar después de una venta
    const handleVentaRealizada = () => {
      cargarAlertaEfectivo();
    };
    window.addEventListener('venta-realizada', handleVentaRealizada);

    return () => {
      clearInterval(interval);
      window.removeEventListener('venta-realizada', handleVentaRealizada);
    };
  }, [cargarAlertaEfectivo]);

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
  };

  const toggleUserMenu = () => {
    setShowUserMenu(!showUserMenu);
    setShowWarehouseMenu(false);
  };

  const toggleWarehouseMenu = () => {
    setShowWarehouseMenu(!showWarehouseMenu);
    setShowUserMenu(false);
  };

  const handleWarehouseChange = (warehouse) => {
    setActiveWarehouse(warehouse);
    setShowWarehouseMenu(false);
  };

  // Cerrar menús cuando se haga clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && !event.target.closest('.user-menu')) {
        setShowUserMenu(false);
      }
      if (showWarehouseMenu && !event.target.closest('.warehouse-menu')) {
        setShowWarehouseMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu, showWarehouseMenu]);

  return (
    <header className="header">
      <div className="header-left">
        <h1 className="header-title">{title}</h1>
      </div>



      <div className="header-right">
        {/* Alerta de Efectivo Excedido */}
        {alertaEfectivo.excedido && (
          <div className="header-alert efectivo-excedido" title={`Efectivo: $${alertaEfectivo.totalEfectivo.toLocaleString('es-MX', { minimumFractionDigits: 2 })} / Límite: $${alertaEfectivo.limiteMaximo.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`}>
            <AlertTriangle size={18} />
            <span className="alert-text">EFECTIVO EXCEDIDO</span>
          </div>
        )}

        {/* Selector de Almacén - Solo si el usuario tiene almacenes */}
        {user?.almacenes && user.almacenes.length > 0 && (
          <div className="warehouse-menu">
            <button
              className="header-btn warehouse-btn"
              onClick={toggleWarehouseMenu}
              title="Cambiar almacén"
            >

              <div className="warehouse-info">
                <span className="warehouse-name">
                  {activeWarehouse?.nombre || 'Seleccionar almacén'}
                </span>
              </div>
              {user.almacenes.length > 1 && (
                <ChevronDown size={16} className={`chevron ${showWarehouseMenu ? 'rotated' : ''}`} />
              )}
            </button>

            {showWarehouseMenu && user.almacenes.length > 1 && (
              <div className="warehouse-dropdown">
                <div className="dropdown-header">
                  <Warehouse size={24} />
                  <div className="warehouse-details">
                    <div className="warehouse-title">Seleccionar Almacén</div>
                  </div>
                </div>

                <div className="dropdown-divider"></div>

                {user.almacenes.map((warehouse) => (
                  <button
                    key={warehouse.id}
                    className={`dropdown-item ${activeWarehouse?.id === warehouse.id ? 'active' : ''}`}
                    onClick={() => handleWarehouseChange(warehouse)}
                  >

                    <div className="warehouse-item-info">
                      <span className="warehouse-item-name">{warehouse.nombre}</span>
                      {warehouse.codigo && (
                        <span className="warehouse-item-code">{warehouse.codigo}</span>
                      )}
                    </div>
                    {activeWarehouse?.id === warehouse.id && (
                      <span className="warehouse-active-check">✓</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="user-menu">
          <button
            className="header-btn user-btn"
            onClick={toggleUserMenu}
          >
            <User size={20} />
            <div className="user-info">
              <span className="user-name">{user?.nombre || 'Usuario'}</span>
            </div>
            <ChevronDown size={16} className={`chevron ${showUserMenu ? 'rotated' : ''}`} />
          </button>

          {showUserMenu && (
            <div className="user-dropdown">
              <div className="dropdown-header">
                <div className="user-avatar">
                  <User size={24} />
                </div>
                <div className="user-details">
                  <div className="user-name">{user?.nombre || 'Usuario'}</div>
                  <div className="user-email">{user?.email || 'Sin email'}</div>
                </div>
              </div>

              <div className="dropdown-divider"></div>

              <button className="dropdown-item">
                <Settings size={18} />
                <span>Configuración</span>
              </button>

              <div className="dropdown-divider"></div>

              <button className="dropdown-item logout-item" onClick={handleLogout}>
                <LogOut size={18} />
                <span>Cerrar Sesión</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;